/* eslint-disable no-underscore-dangle */
import React, { FormEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { FieldControl, FieldGroup, FormBuilder, Validators } from 'react-reactive-form';
import { toast } from 'react-toastify';
import { AppState } from '../../../../store/store.types';
import { NumberInput, TextInput } from '../../../../components/forms/Input';
import StatusLabel from '../../../../components/forms/StatusLabel';
import Checkbox from '../../../../components/forms/Checkbox';
import ConfirmationModal from '../../../../components/modals/ConfirmationModal';
import Breadcrumbs from '../../../../components/breadcrumbs/Breadcrumbs';
import Select from '../../../../components/forms/Select';
import { DATE_FORMAT, SelectedOption, StatusType } from '../../../../api/api.types';
import {
  ExcessModel,
  ItemPriceCarClassModel,
  itemPriceEditRoles,
  ItemPriceModel,
  ItemPriceRequest,
  UnitType,
  UnitTypeOptions
} from '../../../../api/pricing/pricing.types';
import { ItemPriceActionTypes } from '../../types/itemprice.types';
import { addNewItemPrice, deleteItemPrice, editItemPrice, getItemPrice } from '../../actions/itemprice.actions';
import { CarClassModel, ItemGroupType, ItemModel } from '../../../../api/core/core.types';
import { AdminApi } from '../../../../api/api.apiclient';
import { getAllItems } from '../../../setup/actions/item.actions';
import { UserModel } from '../../../../api/authentication/authentication.types';
import { userCanAccessComponent } from '../../../../helpers/authentication.helper';
import { getCarModelsList } from '../../../setup/actions/carmodel.actions';
import ItemPriceCarClassModal from '../../../../components/modals/ItemPriceCarClassModal';
import { generateCarElementLinkForCarClass } from '../../../../helpers/listview.helper';
import { WithTranslation, withTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import DatePicker from '../../../../components/forms/DatePicker';
import { getAllCarClasses } from '../../../core/actions/carClass.actions';
import { ClassifierModel, ClassifierTypes, SupplierModel } from '../../../../api/admin/admin.types';
import infoIcon from '../../../../assets/img/info-circle.svg';
import { UncontrolledTooltip as Tooltip } from 'reactstrap';
import SectionTitle from '../../../../components/forms/SectionTitle';
import { formatDateTime } from '../../../../helpers/date.helper';
import { Link } from 'react-router-dom';

interface ItemPriceLocationProps {
  id: string;
}

interface ItemPriceDetailProps extends RouteComponentProps<ItemPriceLocationProps>, WithTranslation {
  selectedItemPrice: ItemPriceModel | null;
  getSelectedItemPrice: (id: number) => void;
  addNewItemPrice: (request: ItemPriceRequest) => void;
  editItemPrice: (id: number, request: ItemPriceRequest) => void;
  deleteItemPrice: (id: number) => void;
  getItemList: () => void;
  getAllCarClasses: () => void;
  getSupplierInfo: (code: string) => void;
  isNew?: boolean;
  isCopy?: boolean;
  postSuccess: boolean | null;
  lastFetch: number | null;
  items: ItemModel[];
  groupType: ItemGroupType;
  user: UserModel | null;
  carClasses: CarClassModel[];
  supplier: SupplierModel | null;
}

interface ItemPriceDetailState {
  excessCurrencies: ClassifierModel[];
  isDeleteModalOpen: boolean;
  isItemPriceCarClassModalOpen: boolean;
  isEdit: boolean;
  isNew: boolean;
  itemPriceCarClasses: ItemPriceCarClassModel[];
  selectedCarClass: ItemPriceCarClassModel | null;
  acrissCodesInUse: Map<number, string[]>;
  isEditSuccess: boolean;
}

class ItemPriceDetails extends React.Component<ItemPriceDetailProps, ItemPriceDetailState> {

  readonly state = {
    isDeleteModalOpen: false,
    isItemPriceCarClassModalOpen: false,
    excessCurrencies: [] as ClassifierModel[],
    isEdit: false,
    isNew: false,
    itemPriceCarClasses: [] as ItemPriceCarClassModel[],
    selectedCarClass: null,
    acrissCodesInUse: new Map(),
    isEditSuccess: false
  };

  form = FormBuilder.group({
    id: [ null ],
    price: [ null, [ Validators.required, Validators.min(0) ] ],
    isTaxable: [ null ],
    isTaxIncluded: [ null ],
    maxPrice: [ null ],
    unitType: [ null, Validators.required ],
    coreItem: [ null, Validators.required ],
    active: [ true ],
    excessCurrencyCode: [ null ],
    supplierReferenceCode: [ null ],
    excessAmount: [ null, [ Validators.min(0) ] ],
    depositAmount: [ null, [ Validators.min(0) ] ],
    validFrom: [ null ]
  });

  async componentDidMount() {
    const { id } = this.props.match.params;

    await this.props.getItemList();

    if (id) {
      this.props.getSelectedItemPrice(Number(id));
    }

    if (!this.props.carClasses.length) {
      this.props.getAllCarClasses();
    }

    this.setDefaultItem();

    AdminApi.Classifiers.listAllClassifiersByType(ClassifierTypes.CURRENCY).then((response) => {
      this.setState({ excessCurrencies: response });
    });

    if (this.props.isCopy || this.props.isNew) {
      this.setState({ isNew: true });
    }

    if (this.props.isCopy && this.props.selectedItemPrice) {
      this.updateFormValue();
      this.form.controls.validFrom.patchValue(undefined);
    }

    if (this.props.selectedItemPrice) {
      this.updateFormValue();
    }
  }

  componentDidUpdate(prevProps: ItemPriceDetailProps, prevState: ItemPriceDetailState) {
    if ((this.state.isNew) && this.props.postSuccess && this.props.selectedItemPrice) {
      this.props.history.push(`/rates/${this.getCrumb()}/${this.props.selectedItemPrice.id}/view`);
    }

    if (this.props.selectedItemPrice &&
      (!prevProps.selectedItemPrice || this.props.selectedItemPrice !== prevProps.selectedItemPrice)) {
      this.setCarClasses(this.props.selectedItemPrice.carClasses);
      this.updateFormValue();
    }


    if (this.state.isEdit !== prevState.isEdit && this.props.selectedItemPrice && !this.state.isEditSuccess) {
      const carClasses = JSON.parse(JSON.stringify(this.props.selectedItemPrice.carClasses));
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ itemPriceCarClasses: carClasses });
    }

    if (this.state.isEditSuccess) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ isEditSuccess: false });
    }

  }

  setCarClasses = (carClasses: ItemPriceCarClassModel[]) => {
    this.setState({
      itemPriceCarClasses: JSON.parse(JSON.stringify(carClasses)),
      acrissCodesInUse: this.mapMarkedAcrissCodes(carClasses)
    });
  };

  onCarClassSubmitConfirm = (itemPriceCarClass: ItemPriceCarClassModel) => {
    const itemPriceCarClasses: ItemPriceCarClassModel[] = this.state.itemPriceCarClasses;

    if (itemPriceCarClass.itemPriceException && !itemPriceCarClass.itemPriceException.price) {
      itemPriceCarClass.itemPriceException = undefined;
    }

    if (this.state.selectedCarClass) {
      const index = itemPriceCarClasses.findIndex((carClass) => carClass.id === itemPriceCarClass.id);
      delete itemPriceCarClasses[ index ];
      itemPriceCarClasses[ index ] = itemPriceCarClass;

      this.setState({
        itemPriceCarClasses,
        selectedCarClass: null,
        isItemPriceCarClassModalOpen: false,
        acrissCodesInUse: this.mapMarkedAcrissCodes(itemPriceCarClasses)
      });
    } else {
      itemPriceCarClasses.push(itemPriceCarClass);
      this.setState({
        itemPriceCarClasses,
        isItemPriceCarClassModalOpen: false,
        acrissCodesInUse: this.mapMarkedAcrissCodes(itemPriceCarClasses)
      });
    }
  };

  onCarClassDelete(carClassId: number) {
    const itemPriceCarClasses = this.state.itemPriceCarClasses;
    const filteredCarClasses = itemPriceCarClasses.filter((carClass) => carClass.id !== carClassId);
    this.setState({
      itemPriceCarClasses: filteredCarClasses,
      selectedCarClass: null,
      isItemPriceCarClassModalOpen: false,
      acrissCodesInUse: this.mapMarkedAcrissCodes(filteredCarClasses)
    });
  }

  mapMarkedAcrissCodes = (carClasses: ItemPriceCarClassModel[]) => {
    const assignedAcrissCodes = new Map();
    carClasses.forEach((cc) => assignedAcrissCodes.set(cc.id, cc.acrissCodes));

    return assignedAcrissCodes;
  };

  addCarClass = () => {
    this.setState({ isItemPriceCarClassModalOpen: true, selectedCarClass: null });
  };

  editCarClass = (carClassId: number) => {
    const selectedCarClass = this.state.itemPriceCarClasses.find((cc) => cc.id === carClassId);
    if (selectedCarClass) {
      this.setState({ isItemPriceCarClassModalOpen: true, selectedCarClass });
    }
  };

  getCarClasses = (carClasses: ItemPriceCarClassModel[], showForm?: boolean) => {
    const { t } = this.props;

    let carClassList;
    if (carClasses.length === 0) {
      carClassList = (
        <div className="form-group row">
          <div className="col-4 d-flex align-items-start justify-content-end">
            <label>{t('v.all_car_classes')}</label>
          </div>
        </div>
      );
    } else {
      carClassList = (
        <>
          {carClasses.map((carClass: ItemPriceCarClassModel, index: number) => (
            <>
              <div className="form-group row">
                <div className="col-4 d-flex align-items-start justify-content-end">
                  <label>{t('l.item_price.car_classes')}:</label>
                </div>
                {carClass.acrissCodes.length !== 0 && (
                  <div className="col-3 d-flex align-items-center justify-content-start">
                    <ul className="details-list-with-columns">
                      {carClass.acrissCodes.map((acrissCode: string) => (
                        <li
                          key={acrissCode}
                          className="list-link-item"
                          onClick={() => this.props.history.push(
                            generateCarElementLinkForCarClass(acrissCode, this.props.carClasses)
                          )}
                        >
                          {acrissCode}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {!carClass.acrissCodes.length && (
                  <div className="col-6 d-flex align-items-center justify-content-start">
                    <ul className="details-list-with-columns">
                      <div className="form-control-static">{t('v.all_car_classes')}</div>
                    </ul>
                  </div>
                )}
                {showForm && (
                  <button type="button" className="btn btn-link p-0"
                          onClick={(e) => this.editCarClass(carClass.id ?? 0)}>
                    {t('b.edit_Generic edit button')}
                  </button>
                )}
              </div>
              {carClass.itemPriceException && (
                <>
                  <div className="form-group row">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      <label>{t('l.item_price.price_exception')}:</label>
                    </div>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      {this.getPriceWithUnit(carClass.itemPriceException.price)}
                    </div>
                  </div>
                  {carClass.itemPriceException.maxPrice && (
                    <div className="form-group row">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.item_price.max_price_exception')}:</label>
                      </div>
                      <div className="col-4 d-flex align-items-center justify-content-start">
                        {`${carClass.itemPriceException.maxPrice} ${this.props.supplier?.baseCurrencyCode}`}
                      </div>
                    </div>
                  )}
                  {carClass.itemPriceException.supplierReferenceCode && (
                    <div className="form-group row">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.item_price.supplier_reference_code')}:</label>
                      </div>
                      <div className="col-6 d-flex align-items-center justify-content-start">
                        {carClass.itemPriceException.supplierReferenceCode}
                      </div>
                    </div>
                  )}
                </>
              )}
              {index !== (carClasses.length - 1) && <div className="mb-1">
                  <div className="horizontal-dividing-line"/>
              </div>}
            </>
          ))}
        </>
      );
    }

    return carClassList;
  };

  getCrumb = () => {
    switch (this.props.groupType) {
      case ItemGroupType.COVERAGE: {
        return 'protections';
      }
      case ItemGroupType.EXTRA: {
        return 'extras';
      }
      case ItemGroupType.FEE: {
        return 'fees';
      }
      default: {
        return 'itemprices';
      }
    }
  };

  getTitle = () => {
    const { t } = this.props;

    if (this.state.isNew) {
      switch (this.props.groupType) {
        case ItemGroupType.COVERAGE: {
          return t('page_title.add_new_protection');
        }
        case ItemGroupType.EXTRA: {
          return t('page_title.add_new_extra');
        }
        case ItemGroupType.FEE: {
          return t('page_title.add_new_fee');
        }
        default: {
          return t('page_title.add_new_item_price');
        }
      }
    } else {
      switch (this.props.groupType) {
        case ItemGroupType.COVERAGE: {
          return t('page_title.protection_details');
        }
        case ItemGroupType.EXTRA: {
          return t('page_title.extra_details');
        }
        case ItemGroupType.FEE: {
          return t('page_title.fee_details');
        }
        default: {
          return t('page_title.item_price_details');
        }
      }
    }
  };


  setDefaultItem = () => {
    const { t } = this.props;

    if (this.props.selectedItemPrice && this.props.items) {
      const id = this.props.selectedItemPrice.coreItemId;
      const item = this.props.items.find((i) => i.id === id);

      if (item) {
        this.form.controls.coreItem.patchValue({
          value: String(item.id),
          label: item.name + ' (' + item.otaCode + ')'
        });
      }
    }
    this.form.controls.coreItem.patchValue({
      value: String(this.form.controls.coreItem.value), label:
        t('ph.item_price.item')
    });
  };

  getDefaultItem = () => {
    if (this.props.selectedItemPrice && this.props.items) {
      const id = this.props.selectedItemPrice.coreItemId;
      const item = this.props.items.find((i) => i.id === id);

      if (item) {
        return item.name;
      }
    }
    return '';
  };

  getFilteredItems = () => {
    const filteredItems: { value: string; label: string }[] = [];
    this.props.items.forEach((i: ItemModel) => {
      if (i.itemGroupType === this.props.groupType && i.otaCode !== 'FEE_2') {
        filteredItems.push({ value: String(i.id), label: i.name + ' (' + i.otaCode + ')' });
      }
    });

    return filteredItems;
  };

  onDeleteConfirm = async () => {
    const { id } = this.props.match.params;
    await this.props.deleteItemPrice(Number(id));
    this.props.history.push(`/rates/${this.getCrumb()}`);
  };

  onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const form = this.form.controls;
    this.form.markAsSubmitted();
    if (this.form.invalid) {
      return;
    }

    const carClasses: ItemPriceCarClassModel[] = this.state.itemPriceCarClasses;

    let excessModel: ExcessModel | undefined;
    if (form.excessCurrencyCode.value || form.excessAmount.value || form.depositAmount.value) {

      if (form.excessAmount.value && !form.excessCurrencyCode.value) {
        toast(this.props.t('warning_text.item_price.excess_incomplete'),
          { type: 'error', hideProgressBar: true });
        return;
      }

      excessModel = {
        excessCurrencyCode: form.excessCurrencyCode.value,
        excessAmount: form.excessAmount.value,
        depositAmount: form.depositAmount.value
      };
    }

    const request: ItemPriceRequest = {
      coreItemId: form.coreItem.value.id,
      itemGroupType: form.coreItem.value.itemGroupType,
      itemOtaCode: form.coreItem.value.otaCode,
      price: form.price.value,
      maxPrice: form.maxPrice.value,
      isTaxable: form.isTaxable.value === undefined ? false : form.isTaxable.value,
      isTaxIncluded: form.isTaxIncluded.value === undefined ? false : form.isTaxIncluded.value,
      unitType: form.unitType.value,
      statusType: form.active.value ? StatusType.ACTIVE : StatusType.INACTIVE,
      excess: excessModel,
      supplierReferenceCode: form.supplierReferenceCode.value,
      carClasses: carClasses || undefined,
      validFrom: form.validFrom.value.format(DATE_FORMAT)
    };

    if (!this.props.isCopy && this.props.selectedItemPrice) {
      const { id } = this.props.match.params;

      this.props.editItemPrice(Number(id), request);
      this.setState({ isEdit: false, isEditSuccess: true });
    } else {
      this.props.addNewItemPrice(request);
    }
  };

  onCancel = () => {
    if (this.state.isNew) {
      this.props.history.push(`/rates/${this.getCrumb()}`);
    } else {
      this.updateFormValue();
      this.setState({ isEdit: false });
    }
  };

  onEdit = () => {
    this.setState({ isEdit: true });
  };

  onCopy = () => {
    this.props.history.push(`/rates/${this.getCrumb()}/copy`);
  };

  updateFormValue = () => {
    if (!this.props.selectedItemPrice || !this.props.items) {
      return;
    }

    const selectedItemPrice: ItemPriceModel = this.props.selectedItemPrice;
    this.form.patchValue({
      id: selectedItemPrice.id,
      price: selectedItemPrice.price,
      isTaxable: selectedItemPrice.isTaxable,
      isTaxIncluded: selectedItemPrice.isTaxIncluded,
      maxPrice: selectedItemPrice.maxPrice,
      unitType: selectedItemPrice.unitType,
      coreItem: {
        label: this.props.items.find((item) => item.id === selectedItemPrice.coreItemId)!.name + ' (' + this.props.items.find((item) => item.id === selectedItemPrice.coreItemId)!.otaCode + ')',
        value: String(this.props.items.find((item) => item.id === selectedItemPrice.coreItemId)!.id),
        ...this.props.items.find((item) => item.id === selectedItemPrice.coreItemId)
      },
      active: selectedItemPrice.statusType,
      supplierReferenceCode: selectedItemPrice.supplierReferenceCode,
      excessCurrencyCode: selectedItemPrice.excess ? selectedItemPrice.excess.excessCurrencyCode : undefined,
      excessAmount: selectedItemPrice.excess ? selectedItemPrice.excess.excessAmount : undefined,
      depositAmount: selectedItemPrice.excess ? selectedItemPrice.excess.depositAmount : undefined,
      validFrom: dayjs(selectedItemPrice.validFrom)
    });
  };

  getLabel = (list: any[], value: string) => {
    const obj = list.find((select) => select.value === value);
    if (obj) {
      return obj.label;
    }

    return value;
  };

  getPriceWithUnit = (price: number) => {
    const { t } = this.props;
    const priceString = String(price);

    if (this.form.controls.unitType.value === UnitType.PER_CENT) return priceString.concat(' %');

    if (this.form.controls.unitType.value === UnitType.FIXED) {
      return priceString.concat(` ${t('v.item_price.price.fixed',
        { currency: this.props.supplier?.baseCurrencyCode })}`);
    }

    return priceString.concat(` ${t('v.item_price.price.per_day',
      { currency: this.props.supplier?.baseCurrencyCode })}`);
  };


  getExcessCurrency = () => {
    if (this.props.selectedItemPrice && this.props.selectedItemPrice.excess) {
      const excessCurrencyCode = this.props.selectedItemPrice.excess.excessCurrencyCode;
      const excessCurrency = this.state.excessCurrencies.find((select) => select.code === excessCurrencyCode);

      if (excessCurrency) {
        return { value: excessCurrency.code, label: excessCurrency.stringValue };
      }
      return { value: excessCurrencyCode, label: excessCurrencyCode };
    }

    return { value: '', label: this.props.t('ph.item_price.excess_currency') };
  };

  patchField = (option: SelectedOption, field: string) => {
    this.form.patchValue({ [ field ]: option.value });
  };

  patchItem = (option: SelectedOption) => {
    const selItem = this.props.items.find((item) => item.id === Number(option.value));
    if (selItem) {
      this.form.patchValue({
        coreItem: {
          value: String(selItem.id),
          label: selItem.name + ' (' + selItem.otaCode + ')', ...selItem
        }
      });
    }
  };

  findInvalidControls() {
    const invalid: any = [];
    const controls = this.form.controls;
    Object.keys(controls).forEach((k) => {
      if (controls[ k ].invalid) {
        invalid.push(k);
      }
    });
    return invalid;
  }

  render() {
    if (!this.state.isNew && !this.props.selectedItemPrice) {
      return null;
    }

    const showForm = this.state.isEdit || this.state.isNew;
    let canEdit = this.state.isEdit;

    canEdit = !showForm;

    const { t } = this.props;

    return (
      <main>
        <Breadcrumbs
          items={
            [
              { path: '/rates', label: 'Rates' },
              {
                path: `/rates/${this.getCrumb()}`,
                label: this.getCrumb().charAt(0).toUpperCase() + this.getCrumb().slice(1)
              },
              {
                path: null,
                label: this.state.isNew ? 'New' : this.props.selectedItemPrice && String(this.props.selectedItemPrice.id) || ''
              }
            ]
          }
        />
        <div className="page-content">
          <div className="row justify-content-end">
            <div className="col-12 d-flex align-items-center justify-content-between">
              <h1 className="mb-0">{this.getTitle()}</h1>
              {this.props.selectedItemPrice && !showForm && this.props.user && userCanAccessComponent(this.props.user, itemPriceEditRoles)
                && (
                  <div>
                    <button type="button" className="btn btn-danger mr-2"
                            onClick={() => this.setState({ isDeleteModalOpen: true })}>
                      {t('b.delete_Generic delete button').toUpperCase()}
                    </button>
                    <button type="button" className="btn btn-primary edit-button mr-2" onClick={() => this.onCopy()}>
                      {t('b.duplicate').toUpperCase()}
                    </button>
                    <button type="button" className="btn btn-primary edit-button" onClick={() => this.onEdit()}>
                      {t('b.edit_Generic edit button').toUpperCase()}
                    </button>
                  </div>
                )}
            </div>
          </div>
          <FieldGroup
            strict={false}
            control={this.form}
            render={() => (
              <form className="form-horizontal" onSubmit={(e) => this.onSubmit(e)}>
                <hr/>
                <div className="col-12">
                  <div className="form-group d-flex">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      {showForm && (
                        <label className="mandatory-field">* </label>
                      )}
                      <label>{t('l.item_price.item')}:</label>
                    </div>
                    <div className="col-6 d-flex align-items-center justify-content-start padding-zero
                      edit-form-row-input-margins">
                      {
                        this.props.selectedItemPrice && !showForm
                        && (
                          <div className="form-control-static">
                            {this.getDefaultItem()}
                          </div>
                        )
                      }
                      {
                        showForm
                        && (
                          <FieldControl
                            strict={false}
                            name="coreItem"
                            render={(props) => (
                              <Select
                                value={this.form.controls.coreItem.value}
                                className="mr-1"
                                placeholder={t('ph.item_price.item')}
                                options={this.getFilteredItems()}
                                disableClear
                                disableMinWidth
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                label="core item id"
                                onChange={(value) =>
                                  this.patchItem(value as SelectedOption)}
                              />
                            )}
                          />
                        )
                      }
                    </div>
                  </div>
                  <div className="form-group d-flex">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      {showForm && (
                        <label className="mandatory-field">* </label>
                      )}
                      <label>{t('l.item_price.price')}:</label>
                    </div>
                    <div className="col-6 d-flex align-items-center justify-content-start row-max-width-item
                      padding-zero edit-form-row-input-margins">
                      {
                        this.props.selectedItemPrice && !showForm
                        && (
                          <div className="form-control-static">
                            {this.getPriceWithUnit(this.form.controls.price.value)}
                          </div>
                        )
                      }
                      {
                        showForm
                        && (
                          <FieldControl
                            name="price"
                            render={NumberInput}
                          />
                        )
                      }
                    </div>
                  </div>
                  <div className="form-group d-flex">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      <label>{t('l.item_price.max_price')}:</label>
                    </div>
                    <div className="col-6 d-flex align-items-center justify-content-start row-max-width-item
                      padding-zero edit-form-row-input-margins">
                      {
                        this.props.selectedItemPrice && !showForm && this.form.controls.maxPrice.value
                        && (
                          <div className="form-control-static">
                            {`${this.form.controls.maxPrice.value} ${this.props.supplier?.baseCurrencyCode}`}
                          </div>
                        )
                      }
                      {
                        showForm
                        && (
                          <FieldControl
                            name="maxPrice"
                            render={NumberInput}
                          />
                        )
                      }
                    </div>
                  </div>
                  <div className="form-group d-flex">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      <label>{t('l.item_price.supplier_reference_code')}:</label>
                    </div>
                    <div className="col-6 d-flex align-items-center justify-content-start row-max-width-item
                      padding-zero edit-form-row-input-margins">
                      {
                        this.props.selectedItemPrice && !showForm
                        && (
                          <div className="form-control-static">
                            {this.form.controls.supplierReferenceCode.value}
                          </div>
                        )
                      }
                      {
                        showForm
                        && (
                          <FieldControl
                            name="supplierReferenceCode"
                            render={TextInput}
                          />
                        )
                      }
                    </div>
                  </div>
                  {showForm && (
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label className="mandatory-field">* </label>
                        <label>{t('l.item_price.unit_type')}:</label>
                      </div>
                      <div className="col-6 d-flex align-items-center justify-content-start row-max-width-item
                      padding-zero edit-form-row-input-margins">
                        <FieldControl
                          name="unitType"
                          render={(props) => (
                            <Select
                              value={UnitTypeOptions.find((select) => select.value === this.form.controls.unitType.value)}
                              className="mr-1"
                              placeholder={t('ph.item_price.unit_type')}
                              options={(UnitTypeOptions)}
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              disableMinWidth
                              disableClear
                              label="unit type"
                              onChange={(value) => this.patchField(value as SelectedOption, 'unitType')}
                            />
                          )}
                        />
                      </div>
                    </div>
                  )}
                  <div className="form-group row">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      {showForm && (
                        <label className="mandatory-field">* </label>
                      )}
                      <label>{t('l.item_price.taxable')}:</label>
                    </div>
                    <div className="col-6 d-flex align-items-center justify-content-start">
                      <FieldControl
                        strict={false}
                        name="isTaxable"
                        render={(props) => <Checkbox {...props} id="isTaxable" className="mt-1"
                                                     disabled={canEdit}></Checkbox>}
                      />
                    </div>
                  </div>
                  <div className="form-group row">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      {showForm && (
                        <label className="mandatory-field">* </label>
                      )}
                      <label>{t('l.item_price.tax_included')}:</label>
                    </div>
                    <div className="col-6 d-flex align-items-center justify-content-start">
                      <FieldControl
                        strict={false}
                        name="isTaxIncluded"
                        render={(props) =>
                          <Checkbox {...props} id="isTaxIncluded" className="mt-1" disabled={canEdit}></Checkbox>}
                      />
                    </div>
                  </div>
                  <div className="form-group row">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      {showForm && (
                        <label className="mandatory-field">* </label>
                      )}
                      <label>{t('l.item_price.status')}:</label>
                    </div>
                    <div className="col-6 d-flex align-items-center justify-content-start">
                      {
                        this.props.selectedItemPrice && !showForm
                        && (
                          <div className="form-control-static">
                            <StatusLabel status={this.props.selectedItemPrice.statusType}></StatusLabel>
                          </div>
                        )
                      }
                      {
                        showForm
                        && (
                          <FieldControl
                            name="active"
                            render={(props) => <Checkbox {...props} id="statusField"
                                                         className="mt-1" disabled={canEdit}>
                              {t('text.status.active')}
                            </Checkbox>}
                          />
                        )
                      }
                    </div>
                  </div>
                  <div className="form-group row">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      {showForm && (
                        <label className="mandatory-field">* </label>
                      )}
                      <label>{t('l.item_price.valid_from')}
                        <img src={infoIcon} className="i-info-icon" id="valid-from-tooltip"/>:</label>
                      <Tooltip
                        className="dark-tooltip"
                        modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                        placement="bottom"
                        target="valid-from-tooltip"
                        autohide={false}
                      >
                        <pre className="dark-tooltip-text">
                          {t('components.item_price_tooltip.valid_from')}
                        </pre>
                      </Tooltip>
                    </div>
                    {
                      this.props.selectedItemPrice && !showForm
                      && (
                        <div className="col-6 d-flex align-items-center justify-content-start">
                          <div className="form-control-static">
                            {this.form.controls.validFrom.value && this.form.controls.validFrom.value.format('DD.MM.YYYY')}
                          </div>
                        </div>
                      )
                    }
                    {
                      showForm
                      && (
                        <div className="col-4 d-flex row-gap">
                          <div className="col padding-zero">
                            <div className="date-picker-container d-flex align-items-end">
                              <DatePicker
                                id="validFrom"
                                isEdit
                                isFilter
                                isInvalid={false}
                                date={this.form.controls.validFrom ? this.form.controls.validFrom.value : undefined}
                                onChange={(val) => {
                                  this.form.patchValue({ validFrom: val });
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )
                    }
                  </div>
                  {this.getCrumb() === 'protections' && (
                    <>
                      <div className="form-group d-flex">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label>{t('l.item_price.excess_currency_code')}:</label>
                        </div>
                        <div className="col-6 d-flex align-items-center justify-content-start padding-zero
                        edit-form-row-input-margins">
                          {
                            this.props.selectedItemPrice && !showForm
                            && (
                              <div className="form-control-static">
                                {this.form.controls.excessCurrencyCode.value}
                              </div>
                            )
                          }
                          {
                            showForm
                            && (
                              <FieldControl
                                name="excessCurrencyCode"
                                strict={false}
                                render={(props) => (
                                  <Select
                                    defaultValue={this.getExcessCurrency()}
                                    className="mr-1"
                                    placeholder={t('ph.item_price.excess_currency_code')}
                                    options={this.state.excessCurrencies.map((d) => ({
                                      label: d.stringValue,
                                      value: d.code
                                    }))}
                                    closeMenuOnSelect={false}
                                    disableMinWidth
                                    hideSelectedOptions={false}
                                    label="excess currency code"
                                    onChange={(value) => this.patchField(value as SelectedOption, 'excessCurrencyCode')}
                                  />
                                )}
                              />
                            )
                          }
                        </div>
                      </div>
                      <div className="form-group d-flex">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label>{t('l.item_price.excess_amount')}:</label>
                        </div>
                        <div className="col-6 d-flex align-items-center justify-content-start row-max-width-item
                      padding-zero edit-form-row-input-margins">
                          {
                            this.props.selectedItemPrice && !showForm
                            && (
                              <div className="form-control-static">
                                {this.form.controls.excessAmount.value}
                              </div>
                            )
                          }
                          {
                            showForm
                            && (
                              <FieldControl
                                name="excessAmount"
                                render={NumberInput}
                              />
                            )
                          }
                        </div>
                      </div>
                      <div className="form-group d-flex">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label>{t('l.item_price.deposit_amount')}:</label>
                        </div>
                        <div className="col-6 d-flex align-items-center justify-content-start row-max-width-item
                      padding-zero edit-form-row-input-margins">
                          {this.props.selectedItemPrice && !showForm && (
                            <div className="form-control-static">
                              {this.form.controls.depositAmount.value}
                            </div>
                          )}
                          {showForm && (
                            <FieldControl
                              name="depositAmount"
                              render={NumberInput}
                            />
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
                {showForm
                  ? (<SectionTitle
                    title={t('l.item_price.included_car_classes').toUpperCase()}
                    showButton
                    buttonName={t('b.item_price.add_included_car_classes').toUpperCase()}
                    onEditClick={this.addCarClass}
                  />) : (
                    <SectionTitle
                      title={t('l.item_price.included_car_classes').toUpperCase()}
                    />
                  )
                }
                <div className="mb-2"/>
                {this.getCarClasses(this.state.itemPriceCarClasses, showForm)}
                {this.props.selectedItemPrice && !showForm && (
                  <>
                    <SectionTitle title={t('section_title.audit').toUpperCase()}/>
                    <div className="d-flex">
                      <div className="col-8">
                        <div className="d-flex row info-container">
                          <label
                            className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.created_by')}: </label>
                          <div className="col-4 d-flex align-items-center justify-content-start">
                            {this.props.selectedItemPrice.createdBy ?? 'System'}{this.props.selectedItemPrice.createdAt ? ` @ ${formatDateTime(this.props.selectedItemPrice.createdAt)}` : ''}
                          </div>
                          <label
                            className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.modified_by')}: </label>
                          <div className="col-4 d-flex align-items-center justify-content-start">
                            {this.props.selectedItemPrice.modifiedBy ?? 'System'}{this.props.selectedItemPrice.modifiedAt ? ` @ ${formatDateTime(this.props.selectedItemPrice.modifiedAt)}` : ''}
                          </div>
                          <label
                            className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.log')}: </label>
                          <div className="col-4 d-flex align-items-center justify-content-start">
                            <Link to={`/rates/itemprices/${this.props.selectedItemPrice.id}/audit`}
                                  className="btn btn-text-only"
                                  target="_blank"
                                  rel="noopener noreferrer">
                              {t('b.car_model.view_audit_log')}
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {showForm && (
                  <div className="row">
                    <div className="col-12 text-right">
                      <button type="button" className="btn btn-outline-secondary btn-lg mr-2" onClick={this.onCancel}>
                        {t('b.cancel_Generic cancel button translation').toUpperCase()}
                      </button>
                      <button type="submit" className="btn btn-primary btn-lg" onClick={this.onSubmit}>
                        {t('b.save_Generic save button translation').toUpperCase()}
                      </button>
                    </div>
                  </div>
                )}
              </form>
            )}
          />
        </div>
        <ItemPriceCarClassModal
          isOpen={this.state.isItemPriceCarClassModalOpen}
          itemPriceCarClass={this.state.selectedCarClass}
          carClasses={this.props.carClasses}
          toggle={() => this.setState({ isItemPriceCarClassModalOpen: false })}
          onConfirm={(carClass: ItemPriceCarClassModel) => this.onCarClassSubmitConfirm(carClass)}
          onCancel={() => this.setState({ isItemPriceCarClassModalOpen: false, selectedCarClass: null })}
          onItemDelete={(carClassId: number) => this.onCarClassDelete(carClassId)}
          acrissCodesInUse={this.state.acrissCodesInUse}
          priceCurrency={this.props.supplier?.baseCurrencyCode}
          priceType={this.form.controls.unitType.value}
        />
        <ConfirmationModal
          title={t('modal_text.item_price.confirm_removal.title')}
          content={t('modal_text.item_price.confirm_removal.content')}
          isOpen={this.state.isDeleteModalOpen}
          toggle={() => this.setState({ isDeleteModalOpen: false })}
          onConfirm={() => this.onDeleteConfirm()}
          onCancel={() => this.setState({ isDeleteModalOpen: false })}
        />
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  selectedItemPrice: state.rates.itemPrices.selectedItemPrice,
  postSuccess: state.rates.itemPrices.postSuccess,
  lastFetch: state.rates.itemPrices.lastFetch,
  items: state.setup.items.items,
  carModels: state.setup.carModels.carModels,
  carClasses: state.core.carClasses.acrissCodes,
  user: state.authentication.user,
  supplier: state.authentication.activeSupplier
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<{}, {}, ItemPriceActionTypes>
) => ({
  getSelectedItemPrice: (id: number) => dispatch(getItemPrice(id)),
  addNewItemPrice: (request: ItemPriceRequest) => dispatch(addNewItemPrice(request)),
  editItemPrice: (id: number, request: ItemPriceRequest) => dispatch(editItemPrice(id, request)),
  deleteItemPrice: (id: number) => dispatch(deleteItemPrice(id)),
  getItemList: () => dispatch(getAllItems()),
  getCarModelList: () => dispatch(getCarModelsList()),
  getAllCarClasses: () => dispatch(getAllCarClasses())
});

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(ItemPriceDetails));
